// bluaIQ — Financial Trust P0 (PR-F4A): Money Precision Decimal Readiness Adapter.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHAT THIS IS (and is NOT) — read before extending.
// ─────────────────────────────────────────────────────────────────────────────
// This module is an ADAPTER ONLY. It introduces a single, deterministic money
// boundary based on INTEGER CENTS, so that a future Decimal/cents migration and a
// future real scheduled-payment create-flow have a safe, tested foundation.
//
// THIS PR (PR-F4A) DOES NOT:
//   • migrate any Prisma schema (both fields were Float at the time of this PR;
//     Payment.amount later became Decimal(14,2) in PR-F4D and Contract.totalAmount
//     in PR-F4F — this adapter needed no change for either, by design),
//   • change any production persistence or write path,
//   • create real Payment rows,
//   • add a scheduled-payments create endpoint,
//   • authorize the create-flow (it remains BLOCKED until cents/Decimal
//     persistence is actually in place),
//   • touch Budget / Dashboard / Calendar-Gantt / QuickBooks / AIA write
//     behavior / Client Portal / Integrations / AI Chat.
//
// It is purely additive: new helpers + tests. Nothing imports it into a write
// path yet. Adopt it incrementally in later, separately-authorized PRs.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY CENTS (not JS float).
// ─────────────────────────────────────────────────────────────────────────────
// JS numbers are IEEE-754 binary floats and cannot exactly represent most decimal
// money values (0.1 + 0.2 === 0.30000000000000004). Summing, splitting, or
// rounding money as floats accumulates drift. Representing every amount as an
// integer number of cents makes arithmetic exact and deterministic.
//
// REPRESENTATION CONTRACT:
//   • The canonical in-memory money type is an INTEGER number of cents (a JS
//     number that is a SAFE integer per Number.isSafeInteger; $123.45 === 12345
//     cents). Every cents value entering OR leaving a helper boundary is validated
//     by isSafeCents — fractional, non-finite, or beyond-safe-range cents are
//     never produced (parse helpers reject; cents-in helpers throw on programmer
//     error). Exponent-notation numbers (e.g. 1e-7, 1e21) cannot safely become
//     cents and are rejected rather than silently rounded to 0 or to a lossy value.
//   • STRINGS are treated as the AUTHORITATIVE human/DB representation: a money
//     string with more than 2 decimal places is REJECTED by default (we do not
//     silently drop a customer's sub-cent precision). Opt in with
//     `roundSubCent: true` to round it half-away-from-zero to the nearest cent.
//   • NUMBERS are treated as INHERENTLY LOSSY: a number input is normalized via
//     its shortest round-trip string (`String(n)`) and then rounded to the
//     nearest cent. This removes float noise (0.1 + 0.2 → 30 cents) and is the
//     reason callers that need exactness should pass strings, not numbers.
//
// ─────────────────────────────────────────────────────────────────────────────
// FUTURE API / SERIALIZATION CONTRACT (documented, enforced by serializeCentsForApi).
// ─────────────────────────────────────────────────────────────────────────────
// When money crosses the API boundary to the frontend, the PREFERRED future
// contract is a fixed 2-decimal STRING (e.g. "123.45") plus the integer `cents`,
// NEVER a raw Prisma `Decimal` object and NEVER a bare float. A Decimal object
// serializes unpredictably across the wire and a float reintroduces drift on the
// client. `serializeCentsForApi` returns exactly `{ cents, amount }` for this.
//
// Pure + framework-free → unit-testable (see prf4aMoneyPrecisionAdapter.smoke.ts)
// and safe to import from both backend routes and frontend components.

export type MoneyParseReason =
  | 'missing' //            null / undefined
  | 'empty' //              empty/whitespace string (and not allowEmptyAsZero)
  | 'not-finite' //         NaN / Infinity / -Infinity (number input)
  | 'invalid-type' //       not a string or number
  | 'malformed-string' //   string is not a clean money literal
  | 'negative' //           value is negative and allowNegative was not set
  | 'sub-cent-precision' // string has >2 decimals and roundSubCent was not set
  | 'unsafe-cents' //       cannot become a finite SAFE-INTEGER number of cents:
  //                        an exponent-notation number (e.g. 1e-7 → sub-cent,
  //                        1e21 → too large), or a magnitude beyond ±(2^53−1).

export type MoneyParseResult =
  | { ok: true; cents: number }
  | { ok: false; reason: MoneyParseReason }

export interface ParseMoneyOptions {
  /** Permit negative amounts (default false → negatives rejected). */
  allowNegative?: boolean
  /** Treat '' / whitespace-only string as 0 cents (default false → rejected). */
  allowEmptyAsZero?: boolean
  /**
   * For STRING input only: round a >2-decimal value half-away-from-zero to the
   * nearest cent instead of rejecting it (default false → rejected). Number input
   * is always rounded regardless of this flag (numbers are inherently lossy).
   */
  roundSubCent?: boolean
}

export type SplitMoneyResult =
  | { ok: true; lines: number[] }
  | {
      ok: false
      reason:
        | 'invalid-total' //          totalCents not a non-negative safe integer
        | 'empty-percentages' //      no percentages supplied
        | 'invalid-percentage' //     a percentage is NaN/Infinity/not a number
        | 'negative-percentage' //    a percentage is < 0
        | 'percentage-over-100' //    a single percentage is > 100
        | 'percentages-sum-not-100' //sum ≠ 100 and allowRemainder not set
        | 'percentages-sum-over-100' //sum > 100 in allowRemainder mode
        | 'split-invariant' //        defensive: a computed line was negative/unsafe
        //                            or the lines did not sum to total (unreachable)
    }

export interface SplitMoneyOptions {
  /**
   * Allow the percentages to sum to LESS than 100 (the unallocated remainder is
   * simply not distributed). Default false → the sum must be 100 (±tolerance) or
   * the split is rejected. A sum > 100 is rejected in both modes.
   */
  allowRemainder?: boolean
}

/** Percent-sum tolerance so 33.33 + 33.33 + 33.34 is accepted as "100". */
const PERCENT_SUM_TOLERANCE = 0.01

/**
 * The single source of truth for "is this a trustworthy cents value?": a finite
 * SAFE integer (|x| ≤ Number.MAX_SAFE_INTEGER ≈ 9.0e15 cents ≈ $90 trillion).
 * Every helper that accepts or returns cents validates through this — fractional
 * cents (0.5), non-finite cents (NaN/Infinity), and beyond-safe-range cents are
 * all rejected. Money cents must ALWAYS be safe integers.
 */
export function isSafeCents(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value)
}

// ── internal ─────────────────────────────────────────────────────────────────

/**
 * Parse a clean money string into signed cents using INTEGER arithmetic on the
 * digit substrings (never `parseFloat(s) * 100`, which drifts). The string must
 * already be sign/symbol/comma-stripped to `[-]?\d+(\.\d+)?`.
 * Returns null only if `roundSubCent` is false and the fraction exceeds 2 digits.
 */
function decimalStringToCents(clean: string, roundSubCent: boolean): number | null {
  const negative = clean.startsWith('-')
  const unsigned = negative ? clean.slice(1) : clean
  const [intPartRaw, fracPartRaw = ''] = unsigned.split('.')
  const intPart = intPartRaw === '' ? '0' : intPartRaw

  let centsFraction: number
  let carry = 0

  if (fracPartRaw.length <= 2) {
    // Pad to exactly 2 digits: "5" → "50", "" → "00".
    centsFraction = Number((fracPartRaw + '00').slice(0, 2))
  } else {
    if (!roundSubCent) return null
    const keep = Number(fracPartRaw.slice(0, 2))
    const nextDigit = Number(fracPartRaw[2])
    if (nextDigit >= 5) {
      const bumped = keep + 1
      if (bumped === 100) {
        centsFraction = 0
        carry = 1 // 9.999 → 10.00
      } else {
        centsFraction = bumped
      }
    } else {
      centsFraction = keep
    }
  }

  const dollars = Number(intPart) + carry
  const magnitude = dollars * 100 + centsFraction
  return negative ? -magnitude : magnitude
}

// ── public API ───────────────────────────────────────────────────────────────

/**
 * Parse money from a string | number | null | undefined into INTEGER CENTS.
 *
 * Returns an explicit result object — never throws, never silently coerces:
 *   { ok: true, cents } | { ok: false, reason }
 *
 * Rejects (by default): null/undefined, empty string, NaN/±Infinity, invalid
 * type, malformed string, negative value, and >2-decimal string precision.
 * See ParseMoneyOptions to opt into negatives, empty-as-zero, or sub-cent rounding.
 */
export function parseMoneyToCents(value: unknown, opts: ParseMoneyOptions = {}): MoneyParseResult {
  const { allowNegative = false, allowEmptyAsZero = false, roundSubCent = false } = opts

  if (value === null || value === undefined) return { ok: false, reason: 'missing' }

  let clean: string
  let numberInput = false

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return { ok: false, reason: 'not-finite' }
    // Normalize a lossy float via its shortest round-trip string, then round.
    clean = String(value)
    // String() uses EXPONENT notation only for |value| < 1e-6 or |value| >= 1e21.
    // Both extremes cannot safely become integer cents (too small → sub-cent that
    // would silently round to 0; too large → beyond the safe-integer range), and
    // exponent text would also bypass the decimal parser. Reject explicitly rather
    // than coerce — never turn a non-zero amount into 0 cents.
    if (/[eE]/.test(clean)) return { ok: false, reason: 'unsafe-cents' }
    numberInput = true
  } else if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === '') {
      return allowEmptyAsZero ? { ok: true, cents: 0 } : { ok: false, reason: 'empty' }
    }
    // Accept optional sign, optional leading $, thousands commas, one decimal group.
    const stripped = trimmed.replace(/^(-?)\$/, '$1').replace(/,/g, '')
    if (!/^-?\d+(\.\d+)?$/.test(stripped)) return { ok: false, reason: 'malformed-string' }
    clean = stripped
  } else {
    return { ok: false, reason: 'invalid-type' }
  }

  // Numbers are inherently lossy → always permitted to round to the nearest cent.
  const cents = decimalStringToCents(clean, roundSubCent || numberInput)
  if (cents === null) return { ok: false, reason: 'sub-cent-precision' }
  // Final invariant: a successful parse ALWAYS yields finite safe-integer cents.
  // Catches oversized magnitudes (e.g. a 20-digit string) that integer math turns
  // into a non-safe / non-integer float.
  if (!isSafeCents(cents)) return { ok: false, reason: 'unsafe-cents' }
  if (cents < 0 && !allowNegative) return { ok: false, reason: 'negative' }

  return { ok: true, cents }
}

/**
 * Deterministically round any money input to integer cents. Thin wrapper over
 * parseMoneyToCents with sub-cent rounding always enabled (half-away-from-zero).
 * Use for computed values where sub-cent precision is expected and acceptable.
 */
export function roundToCents(value: unknown, opts: Omit<ParseMoneyOptions, 'roundSubCent'> = {}): MoneyParseResult {
  return parseMoneyToCents(value, { ...opts, roundSubCent: true })
}

/**
 * Convert integer cents → a plain fixed 2-decimal money string ("123.45",
 * "-123.45", "0.00"). No currency symbol, no thousands grouping — this is the
 * canonical wire/decimal form. Non-integer/non-finite cents throw, because that
 * indicates a programming error (cents must always be an integer).
 */
export function centsToMoneyString(cents: number): string {
  if (!isSafeCents(cents)) {
    throw new Error(`centsToMoneyString: expected safe-integer cents, got ${cents}`)
  }
  const negative = cents < 0
  const abs = Math.abs(cents)
  const dollars = Math.floor(abs / 100)
  const fraction = abs % 100
  return `${negative ? '-' : ''}${dollars}.${String(fraction).padStart(2, '0')}`
}

/**
 * Format integer cents for DISPLAY: thousands grouping + currency symbol
 * ("$1,234.50", "-$1,234.50"). Locale/currency-symbol configurable; defaults to
 * en-US "$". Display-only — never use the grouped output for storage/wire.
 */
export function formatMoneyFromCents(
  cents: number,
  opts: { locale?: string; currencySymbol?: string } = {},
): string {
  if (!isSafeCents(cents)) {
    throw new Error(`formatMoneyFromCents: expected safe-integer cents, got ${cents}`)
  }
  const { locale = 'en-US', currencySymbol = '$' } = opts
  const negative = cents < 0
  const abs = Math.abs(cents)
  const dollars = Math.floor(abs / 100)
  const fraction = abs % 100
  const groupedDollars = dollars.toLocaleString(locale)
  return `${negative ? '-' : ''}${currencySymbol}${groupedDollars}.${String(fraction).padStart(2, '0')}`
}

/**
 * Serialize integer cents for the API boundary using the PREFERRED future
 * contract: an integer `cents` plus a fixed 2-decimal `amount` string. NEVER emit
 * a raw Prisma Decimal object or a bare float across the wire. The frontend reads
 * `amount` for display and `cents` for exact math.
 */
export function serializeCentsForApi(cents: number): { cents: number; amount: string } {
  if (!isSafeCents(cents)) {
    throw new Error(`serializeCentsForApi: expected safe-integer cents, got ${cents}`)
  }
  return { cents, amount: centsToMoneyString(cents) }
}

/**
 * Deterministically sum a list of integer-cent amounts. Pure integer addition →
 * no float drift. Non-integer entries throw (programmer error).
 */
export function sumCents(centsList: ReadonlyArray<number>): number {
  let total = 0
  for (const c of centsList) {
    if (!isSafeCents(c)) {
      throw new Error(`sumCents: expected safe-integer cents, got ${c}`)
    }
    total += c
    // Guard the running total too: a sum that overflows the safe-integer range is
    // itself an unsafe cents value and must not be silently returned.
    if (!isSafeCents(total)) {
      throw new Error(`sumCents: running total exceeds safe-integer range (${total})`)
    }
  }
  return total
}

/**
 * Split a total (in integer cents) across a percentage schedule, deterministically.
 *
 * Every successful line is guaranteed to be a NON-NEGATIVE safe integer, and in the
 * default mode the lines sum back to exactly `totalCents`. A scheduled-payment line
 * must never be negative, so the "last line absorbs the remainder" shortcut is NOT
 * used (it can underflow below zero for tiny totals, e.g. split(1,[50,50,0])).
 *
 * Default mode (percentages must sum to 100 ±0.01) uses LARGEST-REMAINDER (Hamilton)
 * allocation:
 *   1. raw share_i = pct_i/100 · total
 *   2. floor each share to cents (never negative; floors sum ≤ total)
 *   3. distribute the leftover cents (total − Σfloor) ONE AT A TIME to the lines
 *      with the largest fractional remainders, deterministic tie-break by original
 *      index. 0% lines have a zero share and are never given cents.
 * This makes Σ lines === total exactly with no negative line. e.g.
 *   split($100.00, [50,25,25])        → [5000, 2500, 2500]
 *   split($100.00, [33.33,33.33,33.34])→ [3333, 3333, 3334]
 *   split(1,       [50,50,0])         → [1, 0, 0]   (was [1,1,-1])
 *
 * `allowRemainder: true`: percentages may sum to LESS than 100. The SAME
 * largest-remainder allocation runs, but only the percentage portion is handed
 * out (round of the raw shares, clamped to ≤ totalCents) — so Σ lines ≤ totalCents
 * and the unallocated remainder stays with the payer; it NEVER over-allocates.
 * When the percentages sum to exactly 100, allowRemainder matches the default
 * exact allocation. A sum > 100 is rejected in BOTH modes; negative or >100
 * individual percentages are always rejected.
 *
 * Returns an explicit result object — never throws on bad input.
 */
export function splitMoneyByPercentages(
  totalCents: number,
  percentages: ReadonlyArray<number>,
  opts: SplitMoneyOptions = {},
): SplitMoneyResult {
  const { allowRemainder = false } = opts

  // Total must be a non-negative SAFE-INTEGER cents value (rejects 0.5, NaN,
  // beyond-safe-range, and negatives).
  if (!isSafeCents(totalCents) || totalCents < 0) {
    return { ok: false, reason: 'invalid-total' }
  }
  if (percentages.length === 0) return { ok: false, reason: 'empty-percentages' }

  let sum = 0
  for (const pct of percentages) {
    if (typeof pct !== 'number' || !Number.isFinite(pct)) return { ok: false, reason: 'invalid-percentage' }
    if (pct < 0) return { ok: false, reason: 'negative-percentage' }
    if (pct > 100) return { ok: false, reason: 'percentage-over-100' }
    sum += pct
  }

  if (sum > 100 + PERCENT_SUM_TOLERANCE) {
    return { ok: false, reason: 'percentages-sum-over-100' }
  }
  if (!allowRemainder && Math.abs(sum - 100) > PERCENT_SUM_TOLERANCE) {
    return { ok: false, reason: 'percentages-sum-not-100' }
  }

  // Final invariant guard (defense in depth): every line must be a non-negative
  // safe integer, and the lines must NEVER allocate more than totalCents (exact in
  // default mode; ≤ in allowRemainder mode — a split must never create money).
  const finalize = (lines: number[], mode: 'exact' | 'at-most'): SplitMoneyResult => {
    let total = 0
    for (const line of lines) {
      if (!isSafeCents(line) || line < 0) return { ok: false, reason: 'split-invariant' }
      total += line
    }
    if (mode === 'exact' ? total !== totalCents : total > totalCents) {
      return { ok: false, reason: 'split-invariant' }
    }
    return { ok: true, lines }
  }

  // Largest-remainder (Hamilton) allocation for BOTH modes — non-negative by
  // construction (floors of non-negative shares, then +1 increments only).
  const shares = percentages.map((pct) => (pct / 100) * totalCents)
  const lines = shares.map((share) => Math.floor(share))
  const baseAllocated = lines.reduce((acc, n) => acc + n, 0)

  // How many cents to hand out beyond the floors:
  //  • default mode forces the FULL total (Σ lines === totalCents, honoring the
  //    ±tolerance treatment of "100%").
  //  • allowRemainder mode allocates ONLY the percentage portion (round of the raw
  //    shares), CLAMPED so it can never exceed totalCents — the unallocated
  //    remainder is intentionally left behind (a split must never over-allocate).
  const target = allowRemainder
    ? Math.min(totalCents, Math.round(shares.reduce((acc, n) => acc + n, 0)))
    : totalCents
  let toDistribute = Math.max(0, target - baseAllocated)

  // Recipients ordered by largest fractional remainder, tie-broken by original
  // index; 0% lines (zero share) are excluded and never receive cents.
  const order = shares
    .map((share, index) => ({ index, frac: share - Math.floor(share), pct: percentages[index] }))
    .filter((entry) => entry.pct > 0)
    .sort((a, b) => b.frac - a.frac || a.index - b.index)

  for (let k = 0; toDistribute > 0 && order.length > 0; k++) {
    lines[order[k % order.length].index] += 1
    toDistribute--
  }

  return finalize(lines, allowRemainder ? 'at-most' : 'exact')
}
